/**
 * Query keys for character-related queries and mutations
 * Used with React Query to cache and invalidate queries
 */

import { normalizeId } from "../utils";

export const characterKeys = {
  // Base key for all character-related queries
  all: ["characters"] as const,

  // Key for listing characters in a project/document
  list: (projectId: string | number, documentId: string | number) =>
    [...characterKeys.all, "list", normalizeId(projectId), normalizeId(documentId)] as const,

  // Key for a specific character details
  detail: (projectId: string | number, documentId: string | number, characterId: string | number) =>
    [
      ...characterKeys.all,
      "detail",
      normalizeId(projectId),
      normalizeId(documentId),
      normalizeId(characterId),
    ] as const,

  // Key for character summary data
  summary: (projectId: string | number, documentId: string | number) =>
    [...characterKeys.all, "summary", normalizeId(projectId), normalizeId(documentId)] as const,

  // Key for all detailed characters
  allDetailed: (projectId: string | number, documentId: string | number) =>
    [...characterKeys.all, "allDetailed", normalizeId(projectId), normalizeId(documentId)] as const,

  // Key for character breakdown data
  breakdown: (
    projectId: string | number,
    documentId: string | number,
    characterId?: string | number
  ) =>
    characterId
      ? ([
          ...characterKeys.all,
          "breakdown",
          normalizeId(projectId),
          normalizeId(documentId),
          normalizeId(characterId),
        ] as const)
      : ([
          ...characterKeys.all,
          "breakdown",
          normalizeId(projectId),
          normalizeId(documentId),
        ] as const),

  // Key for character assignments (actor assignments)
  assignments: (
    projectId: string | number,
    documentId: string | number,
    characterId?: string | number
  ) =>
    characterId
      ? ([
          ...characterKeys.all,
          "assignments",
          normalizeId(projectId),
          normalizeId(documentId),
          normalizeId(characterId),
        ] as const)
      : ([
          ...characterKeys.all,
          "assignments",
          normalizeId(projectId),
          normalizeId(documentId),
        ] as const),

  // Key for character images
  images: (
    projectId: string | number,
    documentId: string | number,
    characterId?: string | number
  ) =>
    characterId
      ? ([
          ...characterKeys.all,
          "images",
          normalizeId(projectId),
          normalizeId(documentId),
          normalizeId(characterId),
        ] as const)
      : ([
          ...characterKeys.all,
          "images",
          normalizeId(projectId),
          normalizeId(documentId),
        ] as const),
};
